import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Flex,
  Spinner,
  Alert,
  AlertIcon,
  Text,
  ButtonGroup,
  useToast,
} from '@chakra-ui/react';
import { patientApi } from '../services/api';

function PatientList() {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const toast = useToast();

  useEffect(() => {
    fetchPatients();
  }, []);

  const fetchPatients = async () => {
    try {
      setLoading(true);
      const response = await patientApi.getAll();
      setPatients(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch patients');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this patient?')) {
      try {
        await patientApi.delete(id);
        toast({
          title: 'Patient deleted',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        fetchPatients();
      } catch (err) {
        toast({
          title: 'Failed to delete patient',
          description: err.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        console.error(err);
      }
    }
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" h="200px">
        <Spinner size="xl" color="brand.purple" thickness="4px" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="lg">
        <AlertIcon />
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="lg">Patients</Heading>
        <Button as={RouterLink} to="/app/patients/new" colorScheme="purple">
          Add New Patient
        </Button>
      </Flex>

      {patients.length === 0 ? (
        <Text color="gray.500">No patients found. Add your first patient!</Text>
      ) : (
        <Box overflowX="auto" bg="white" borderRadius="xl" boxShadow="sm">
          <Table variant="simple">
            <Thead bg="brand.darkPurple">
              <Tr>
                <Th color="white">ID</Th>
                <Th color="white">Name</Th>
                <Th color="white">Date of Birth</Th>
                <Th color="white">Email</Th>
                <Th color="white">Phone</Th>
                <Th color="white" textAlign="right">Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {patients.map((patient) => (
                <Tr key={patient.id} _hover={{ bg: 'brand.lightGray' }}>
                  <Td>{patient.id}</Td>
                  <Td fontWeight="medium">{patient.first_name} {patient.last_name}</Td>
                  <Td>{patient.date_of_birth}</Td>
                  <Td>{patient.email || 'N/A'}</Td>
                  <Td>{patient.phone || 'N/A'}</Td>
                  <Td textAlign="right">
                    <ButtonGroup size="sm" spacing={2}>
                      <Button
                        as={RouterLink}
                        to={`/app/patients/${patient.id}`}
                        colorScheme="purple"
                      >
                        View Details
                      </Button>
                      <Button
                        as={RouterLink}
                        to={`/app/patients/edit/${patient.id}`}
                        variant="outline"
                        colorScheme="purple"
                      >
                        Edit
                      </Button>
                      <Button
                        colorScheme="red"
                        variant="outline"
                        onClick={() => handleDelete(patient.id)}
                      >
                        Delete
                      </Button>
                    </ButtonGroup>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
}

export default PatientList;

